export function isBrowser() {
    return (typeof window === 'object' &&
        typeof document === 'object' &&
        Object.prototype.toString.call(document) === '[object HTMLDocument]');
}
export function getRandomId() {
    return Math.random().toString(36).slice(2);
}
export function getObjectKeys(obj) {
    return Object.keys(obj);
}
export function toStringTag(value) {
    return Object.prototype.toString.call(value);
}
export function hasOwnProperty(target, key) {
    return Object.prototype.hasOwnProperty.call(target, key);
}
export function isString(value) {
    return typeof value === 'string';
}
export function isNumber(value) {
    return typeof value === 'number';
}
export function isBigInt(value) {
    return toStringTag(value) === '[object BigInt]';
}
export function isArray(value) {
    return value instanceof Array;
}
export function isArrayLike(value) {
    if (typeof NodeList === 'function' &&
        NodeList.name === 'NodeList' &&
        value instanceof NodeList) {
        return true;
    }
    if (typeof HTMLCollection === 'function' &&
        HTMLCollection.name === 'HTMLCollection' &&
        value instanceof HTMLCollection) {
        return true;
    }
    return false;
}
export function isObjectLike(value) {
    return typeof value === 'object' && value !== null;
}
export function isPlainObject(value) {
    if (!isObjectLike(value) || toStringTag(value) !== '[object Object]') {
        return false;
    }
    return true;
}
export function isPrototype(value) {
    if (isObjectLike(value) &&
        hasOwnProperty(value, 'constructor') &&
        typeof value.constructor === 'function') {
        return true;
    }
    return false;
}
export function isBlob(value) {
    return value instanceof Blob;
}
export function isArrayBuffer(value) {
    return value instanceof ArrayBuffer;
}
export function isURLSearchParams(value) {
    return value instanceof URLSearchParams;
}
export function isFormData(value) {
    return value instanceof FormData;
}
export function isFile(value) {
    return value instanceof File;
}
export function isHeaders(value) {
    return value instanceof Headers;
}
export function isDocument(value) {
    return value instanceof Document;
}
export function isURL(value) {
    return value instanceof URL;
}
export function isClass(obj) {
    return typeof obj === 'function' && typeof obj.prototype !== 'undefined';
}
const CN_IDs = ['zh-CN', 'zh-HK', 'zh-TW', 'zh', 'zh-Hans-CN'];
export function isCN() {
    if (isBrowser()) {
        const { lang } = document.documentElement;
        if (lang)
            return CN_IDs.some((i) => i === lang);
        return CN_IDs.some((i) => i === navigator.language);
    }
    return false;
}
export function isTypedArray(value) {
    return ArrayBuffer.isView(value);
}
const stringify = (value) => `${value}`;
const primitive = (value) => ({
    ok: true,
    value,
});
export function makePrimitiveValue(value) {
    if (value === undefined) {
        return primitive(stringify(value));
    }
    if (value === null) {
        return primitive(value);
    }
    if (isNumber(value)) {
        if (value === -Infinity || value === Infinity || Number.isNaN(value)) {
            return primitive(stringify(value));
        }
    }
    if (isBigInt(value)) {
        return primitive(`${value}n`);
    }
    if (typeof value === 'symbol' || typeof value === 'function') {
        return primitive(stringify(value.toString()));
    }
    if (value instanceof Error) {
        return primitive(stringify(value.stack));
    }
    if (value === Object.prototype) {
        return {
            value: null,
            ok: false,
        };
    }
    if (!(value instanceof Object || typeof value === 'object')) {
        return primitive(value);
    }
    return {
        value,
        ok: false,
    };
}
/**
 * convert `symbol / error / undefined / function` type data to readable string content
 */
export function stringifyData(data) {
    const { ok, value } = makePrimitiveValue(data);
    /* c8 ignore next 3 */
    if (ok) {
        return value;
    }
    return JSON.stringify(data, (key, val) => makePrimitiveValue(val).value, 2);
}
export function getValueType(value) {
    if (value === undefined)
        return 'undefined';
    if (value === null)
        return 'null';
    if (isBigInt(value))
        return 'bigint';
    if (value instanceof Object) {
        if (value instanceof Error)
            return 'error';
        // Here we do not use 'instanceof Function' because in some context
        // like mini program the Function constructor is overwritten.
        if (typeof value === 'function')
            return 'function';
        return 'object';
    }
    return typeof value;
}
const unproxyConsole = { ...console };
export const psLog = ['log', 'info', 'error', 'warn', 'debug'].reduce((result, method) => {
    result[method] = (...message) => {
        console[method](`[PageSpy] [${method.toLocaleUpperCase()}] `, ...message);
    };
    result.unproxy[method] = (...message) => {
        unproxyConsole[method](`[PageSpy] [${method.toLocaleUpperCase()}] `, ...message);
    };
    return result;
}, { unproxy: {} });
export const removeEndSlash = (s) => {
    return s.replace(/\/$/, '');
};
export function getAuthSecret() {
    const secret = Math.floor(Math.random() * 1000000);
    return String(secret).padStart(6, '0');
}
export const formatErrorObj = (err) => {
    if (typeof err !== 'object')
        return null;
    const { name, message, stack } = Object(err);
    if ([name, message, stack].every(Boolean) === false) {
        return null;
    }
    return {
        name,
        message,
        stack,
    };
};
export const blob2base64Async = (blob) => {
    return new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = (e) => {
            resolve(e.target?.result);
        };
        /* c8 ignore next 3 */
        fr.onerror = () => {
            reject(new Error('blob2base64Async: can not convert'));
        };
        fr.readAsDataURL(blob);
    });
};
